/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { performanceMonitor } from '../utils/performanceMonitor';
import logger from '../utils/logger';

// Conditional Pusher import to prevent crashes if not configured
let Pusher: any = null;
try {
  if (typeof window !== 'undefined' && process.env.REACT_APP_PUSHER_KEY) {
    Pusher = require('pusher-js');
  }
} catch (error: any) {
  logger.warn('Pusher not available:', error.message);
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Fix API paths to avoid duplicate /api
const makeApiPath = (path: string) => {
  // If we're getting 404s from the API, log a more helpful message
  if (process.env.NODE_ENV === 'development') {
    logger.info(`API call to: ${API_URL}${path.startsWith('/') ? path : '/' + path}`);
  }
  
  // If API_URL already includes /api, don't add it again
  if (API_URL.endsWith('/api')) {
    return `${API_URL}${path.startsWith('/') ? path : '/' + path}`;
  }
  
  // Otherwise, include /api in the path
  return `${API_URL}/api${path.startsWith('/') ? path : '/' + path}`;
};

// Types
export type User = {
  id: string;
  username: string;
  email: string;
  trustedUser?: boolean;
  isEmailVerified?: boolean;
  cubePoints?: number;
  accountStatus?: string;
};

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
  login: (email: string, password: string) => Promise<void>;
  register: (username: string, email: string, password: string, captchaToken?: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<boolean>;
  redirectAfterLogin: (path: string) => void;
  getRedirectPath: () => string | null;
  clearRedirectPath: () => void;
  clearError: () => void;
  resendVerificationEmail: () => Promise<void>;
  refreshUserData: () => Promise<void>;
  // Add aliases for App.tsx compatibility
  handleLogin: (token: string) => Promise<void>;
  handleLogout: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Debug error state changes
  useEffect(() => {
    logger.info('📢 Error state changed:', error);
  }, [error]);
  const [token, setToken] = useState<string | null>(null);
  const [pusherConnection, setPusherConnection] = useState<any>(null);

  // Initialize auth state on component mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadUser = async () => {
      // Check if token exists in localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      // Check if we've recently validated the user (within 5 minutes)
      const lastAuthCheck = localStorage.getItem('last_auth_check');
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      
      if (lastAuthCheck && parseInt(lastAuthCheck) > fiveMinutesAgo) {
        // Try to get user from localStorage cache first
        const cachedUser = localStorage.getItem('cached_user');
        if (cachedUser) {
          try {
            const userData = JSON.parse(cachedUser);
            setUser(userData);
            setToken(token);
            setLoading(false);
            logger.info('AuthContext: Using cached user data - NO API call');
            performanceMonitor.trackAuthCacheHit();
            // Still set up Pusher connection for real-time updates
            setupPusherConnection(userData.id);
            return;
          } catch (e) {
            logger.warn('Failed to parse cached user data');
            localStorage.removeItem('cached_user');
            localStorage.removeItem('last_auth_check');
          }
        }
      }

      // If we reach here, we need to make an API call
      logger.info('AuthContext: Cache miss - making API call to validate user');
      performanceMonitor.trackAuthApiCall();

      try {
        // Set Authorization header for all axios requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        // Get current user
        const res = await axios.get(makeApiPath('/auth/user'));
        
        // Map the _id field to id for our User type
        let userData;
        if (res.data && res.data._id) {
          // Create a new object with the expected 'id' field
          userData = {
            id: String(res.data._id), // Ensure id is a string
            username: res.data.username,
            email: res.data.email,
            trustedUser: res.data.trustedUser,
            isEmailVerified: res.data.isEmailVerified || false,
            cubePoints: res.data.cubePoints ?? 0,
            accountStatus: res.data.accountStatus
          };
          
          logger.info('AuthContext: Mapped _id to id:', {
            _id: res.data._id,
            id: String(res.data._id)
          });
        } else {
          userData = res.data;
        }
        
        setUser(userData);
        setToken(token);
        
        // Cache user data and timestamp
        localStorage.setItem('cached_user', JSON.stringify(userData));
        localStorage.setItem('last_auth_check', Date.now().toString());
        logger.info('AuthContext: User data cached for 5 minutes');
        
        // Set up Pusher connection for real-time updates
        setupPusherConnection(userData.id);
        
      } catch (err: any) {
        logger.error('Error loading user:', err);
        
        // Handle 404 errors more gracefully - this might happen if the API server is offline
        if (err.response && err.response.status === 404) {
          logger.warn('API server may be offline or the endpoint may not exist. Check your API_URL setting.');
          
          // In development, you can create a fake user for testing
          if (process.env.NODE_ENV === 'development') {
            logger.info('Development mode: Creating a mock user for testing');
            // Keep current user if exists, otherwise create a mock one
            if (!user) {
              setUser({
                id: 'dev-user-1',
                username: 'DevelopmentUser',
                email: 'dev@example.com'
              });
            }
            setLoading(false);
            return; // Exit early to keep the token
          }
        }
        
        // Clear localStorage on auth error
        localStorage.removeItem('token');
        localStorage.removeItem('cached_user');
        localStorage.removeItem('last_auth_check');
        delete axios.defaults.headers.common['Authorization'];
      } finally {
        setLoading(false);
      }
    };

    loadUser();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run only on mount
  }, []);

  // Set up Pusher connection for real-time user updates
  const setupPusherConnection = (userId: string) => {
    if (!userId || pusherConnection) return;

    try {
      const pusher = new Pusher(process.env.REACT_APP_PUSHER_KEY!, {
        cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'us2'
      });

      const userChannel = pusher.subscribe(`user-${userId}`);
      
      // Listen for user data updates
      userChannel.bind('user_updated', (data: any) => {
        logger.info('AuthContext: Received user update via Pusher:', data);
        if (data.user) {
          setUser(data.user);
          // Update cache
          localStorage.setItem('cached_user', JSON.stringify(data.user));
          localStorage.setItem('last_auth_check', Date.now().toString());
        }
      });

      // Listen for email verification updates
      userChannel.bind('email_verified', (data: any) => {
        logger.info('AuthContext: Email verified via Pusher');
        setUser(prev => prev ? { ...prev, isEmailVerified: true } : null);
        // Update cache
        if (user) {
          const updatedUser = { ...user, isEmailVerified: true };
          localStorage.setItem('cached_user', JSON.stringify(updatedUser));
        }
      });

      setPusherConnection(pusher);
      logger.info('AuthContext: Pusher connection established for user', userId);
    } catch (error) {
      logger.error('AuthContext: Failed to set up Pusher connection:', error);
    }
  };

  // Clean up Pusher connection
  useEffect(() => {
    return () => {
      if (pusherConnection) {
        logger.info('AuthContext: Cleaning up Pusher connection');
        pusherConnection.disconnect();
      }
    };
  }, [pusherConnection]);

  // Register user
  const register = async (username: string, email: string, password: string, captchaToken?: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const requestData: any = {
        username,
        email,
        password
      };
      
      // Include captcha token if provided
      if (captchaToken) {
        requestData.captchaToken = captchaToken;
      }
      
      logger.info('🔄 Attempting registration...', {
        username,
        email,
        hasCaptchaToken: !!captchaToken
      });
      
      const res = await axios.post(makeApiPath('/auth/register'), requestData, {
        timeout: 15000, // 15 second timeout
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      logger.info('✅ Registration successful:', {
        status: res.status,
        hasToken: !!res.data.token,
        hasUser: !!res.data.user
      });
      
      // Check if we actually got a valid response
      if (!res.data.token || !res.data.user) {
        throw new Error('Invalid response from server - missing token or user data');
      }
      
      // Save token to localStorage
      localStorage.setItem('token', res.data.token);
      
      // Set Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      
      // Map the _id / id field to id for our User type (API may return either)
      const rawUser = res.data.user as { _id?: string; id?: string; username?: string; email?: string; isEmailVerified?: boolean; accountStatus?: string };
      const rawId = rawUser._id ?? rawUser.id;
      let registeredUser: User;
      if (rawUser && rawId) {
        registeredUser = {
          id: String(rawId),
          username: rawUser.username!,
          email: rawUser.email!,
          isEmailVerified: rawUser.isEmailVerified || false,
          accountStatus: rawUser.accountStatus
        };
        setUser(registeredUser);
        logger.info('AuthContext: Mapped user in register:', { id: registeredUser.id });
      } else {
        registeredUser = res.data.user as User;
        setUser(registeredUser);
      }
      
      setToken(res.data.token);
      localStorage.setItem('cached_user', JSON.stringify(registeredUser));
      localStorage.setItem('last_auth_check', Date.now().toString());
      try {
        localStorage.setItem(
          'user',
          JSON.stringify({ id: registeredUser.id, username: registeredUser.username, email: registeredUser.email, isEmailVerified: registeredUser.isEmailVerified })
        );
      } catch {
        /* ignore */
      }
    } catch (err: any) {
      logger.error('Registration error:', err);
      
      let errorMessage = 'Registration failed';
      
      // Handle network errors and timeouts
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Registration timeout - please check your connection and try again';
      } else if (err.code === 'NETWORK_ERROR' || err.message?.includes('Network Error')) {
        errorMessage = 'Network error - please check your connection and try again';
      } else if (err.response?.status === 504) {
        errorMessage = 'Server timeout - please try again in a few minutes';
      } else if (err.response?.status === 503) {
        errorMessage = 'Database connection error - please try again';
      } else if (err.response?.status === 502) {
        errorMessage = 'Server error - please try again';
      } else if (err.response?.status === 0) {
        errorMessage = 'Connection failed - please check your internet connection';
      } else if (!err.response) {
        // This handles cases where the request never completed
        errorMessage = 'Request failed - please check your connection and try again';
      } else {
        // Handle structured API errors
        errorMessage = err.response?.data?.message || err.response?.data?.error || 'Registration failed';
      }
      
      setError(errorMessage);
      
      // IMPORTANT: Throw the error so the calling component can handle it
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Login user
  const login = async (email: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      
      const res = await axios.post(makeApiPath('/auth/login'), {
        email,
        password
      });
      
      // Save token to localStorage
      localStorage.setItem('token', res.data.token);
      
      // Set Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      
      // Map the _id field to id for our User type
      let userData;
      if (res.data.user && res.data.user._id) {
        // Create a new object with the expected 'id' field
        userData = {
          id: String(res.data.user._id), // Ensure id is a string
          username: res.data.user.username,
          email: res.data.user.email,
          isEmailVerified: res.data.user.isEmailVerified || false,
          cubePoints: res.data.user.cubePoints ?? 0,
          accountStatus: res.data.user.accountStatus
        };
        
        logger.info('AuthContext: Mapped _id to id in login:', {
          _id: res.data.user._id,
          id: String(res.data.user._id)
        });
      } else {
        userData = res.data.user;
      }
      
      setUser(userData);
      setToken(res.data.token);
      
      // Cache user data and timestamp
      localStorage.setItem('cached_user', JSON.stringify(userData));
      localStorage.setItem('last_auth_check', Date.now().toString());
      
      // Set up Pusher connection for real-time updates
      setupPusherConnection(userData.id);
      
      // Check for redirect path after successful login
      const redirectPath = getRedirectPath();
      if (redirectPath) {
        clearRedirectPath();
        window.location.href = redirectPath;
      }
    } catch (err: any) {
      logger.error('Login error:', err);
      logger.error('Error response status:', err.response?.status);
      logger.error('Error response data:', err.response?.data);
      
      let errorMessage = 'Login failed';
      
      // Handle specific error types
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        errorMessage = 'Login timeout - please check your connection and try again';
      } else if (err.code === 'NETWORK_ERROR' || err.message?.includes('Network Error')) {
        errorMessage = 'Network error - please check your connection and try again';
      } else if (err.response?.status === 504) {
        errorMessage = 'Login timeout - please try again in a few moments';
      } else if (err.response?.status === 503) {
        errorMessage = 'Database connection error - please try again';
      } else if (err.response?.status === 502) {
        errorMessage = 'Server error - please try again';
      } else if (err.response?.status === 0) {
        errorMessage = 'Connection failed - please check your internet connection';
      } else if (!err.response) {
        errorMessage = 'Request failed - please check your connection and try again';
      } else if (err.response?.status === 429) {
        // Handle rate limiting specifically
        const retryAfter = err.response?.data?.retryAfter;
        const minutes = retryAfter ? Math.ceil(retryAfter / 60) : 15;
        const errorMessage = t('loginRateLimit', { minutes });
        const fixedMessage = errorMessage.replace(/{minutes}/g, minutes.toString());
        logger.info('🚫 Setting rate limit error:', fixedMessage);
        setError(fixedMessage);
        return;
      } else {
        // Handle structured API errors
        errorMessage = err.response?.data?.message || err.response?.data?.error || 'Login failed';
      }
      
      logger.info('❌ Setting login error:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Logout user
  const logout = () => {
    // Clean up Pusher connection
    if (pusherConnection) {
      logger.info('AuthContext: Disconnecting Pusher on logout');
      pusherConnection.disconnect();
      setPusherConnection(null);
    }
    
    // Remove token from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('cached_user');
    localStorage.removeItem('last_auth_check');
    
    // Clear Authorization header
    delete axios.defaults.headers.common['Authorization'];
    
    // Reset state
    setUser(null);
    setToken(null);
  };

  // Clear error (memoized so RegisterPage doesn't clear errors on every render)
  const clearError = useCallback(() => {
    logger.info('🔄 Clearing error...');
    setError(null);
  }, []);

  const redirectAfterLogin = (path: string) => {
    localStorage.setItem('redirectAfterLogin', path);
  };

  const getRedirectPath = () => {
    return localStorage.getItem('redirectAfterLogin');
  };

  const clearRedirectPath = () => {
    localStorage.removeItem('redirectAfterLogin');
  };

  const checkAuth = async () => {
    try {
      // Check if token exists in localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        setUser(null);
        setToken(null);
        return false;
      }

      // Check if we've recently validated the user (within 5 minutes)
      const lastAuthCheck = localStorage.getItem('last_auth_check');
      const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
      
      if (lastAuthCheck && parseInt(lastAuthCheck) > fiveMinutesAgo) {
        // Try to get user from localStorage cache first
        const cachedUser = localStorage.getItem('cached_user');
        if (cachedUser) {
          try {
            const userData = JSON.parse(cachedUser);
            setUser(userData);
            setToken(token);
            logger.info('AuthContext: checkAuth using cached data - NO API call');
            performanceMonitor.trackAuthCacheHit();
            return true;
          } catch (e) {
            logger.warn('Failed to parse cached user data in checkAuth');
            localStorage.removeItem('cached_user');
            localStorage.removeItem('last_auth_check');
          }
        }
      }

      // If we reach here, we need to make an API call
      logger.info('AuthContext: checkAuth cache miss - making API call');
      performanceMonitor.trackAuthApiCall();

      // Set Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      try {
        // Get current user from API
        const res = await axios.get(makeApiPath('/auth/user'));
        
        // Map the _id field to id for our User type
        let userData;
        if (res.data && res.data._id) {
          // Create a new object with the expected 'id' field
          userData = {
            id: String(res.data._id), // Ensure id is a string
            username: res.data.username,
            email: res.data.email,
            trustedUser: res.data.trustedUser,
            isEmailVerified: res.data.isEmailVerified || false,
            cubePoints: res.data.cubePoints ?? 0,
            accountStatus: res.data.accountStatus
          };
          
          logger.info('AuthContext: Mapped _id to id:', {
            _id: res.data._id,
            id: String(res.data._id)
          });
        } else {
          userData = res.data;
        }
        
        setUser(userData);
        setToken(token);
        
        // Cache user data and timestamp
        localStorage.setItem('cached_user', JSON.stringify(userData));
        localStorage.setItem('last_auth_check', Date.now().toString());
        logger.info('AuthContext: checkAuth cached user data for 5 minutes');
        
        return true;
      } catch (err) {
        logger.error('Error checking auth:', err);
        // Clear localStorage on auth error
        localStorage.removeItem('token');
        localStorage.removeItem('cached_user');
        localStorage.removeItem('last_auth_check');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        setToken(null);
        return false;
      }
    } catch (err) {
      logger.error('Error in checkAuth:', err);
      setUser(null);
      setToken(null);
      return false;
    }
  };

  // Add more detailed logging to the user state
  useEffect(() => {
    logger.info('AuthContext: User state updated', {
      id: user?.id,
      username: user?.username,
      isAuthenticated: !!user,
      isLoading: loading,
      token: !!token,
      idType: user?.id ? typeof user.id : 'undefined',
      actualIdValue: user?.id,
      userObject: user
    });
  }, [user, loading, token]);

  // Add compatibility methods for App.tsx
  const handleLogin = async (token: string) => {
    try {
      // Save token to localStorage
      localStorage.setItem('token', token);
      
      // Set Authorization header
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Get current user data using the token
      const res = await axios.get(makeApiPath('/auth/user'));
      
      // Map the _id field to id for our User type
      if (res.data && res.data._id) {
        setUser({
          id: String(res.data._id),
          username: res.data.username,
          email: res.data.email,
          trustedUser: res.data.trustedUser,
          isEmailVerified: res.data.isEmailVerified || false,
          cubePoints: res.data.cubePoints ?? 0,
          accountStatus: res.data.accountStatus
        });
      } else {
        setUser(res.data);
      }
      
      setToken(token);
    } catch (err) {
      logger.error('Error in handleLogin:', err);
      throw err;
    }
  };
  
  // Alias logout as handleLogout for App.tsx compatibility
  const handleLogout = logout;

  // Add resendVerificationEmail function
  const resendVerificationEmail = async () => {
    try {
      setError(null);
      setLoading(true);
      
      if (!user?.email) {
        throw new Error('User email not found');
      }
      
      await axios.post(makeApiPath('/auth/resend-verification'), {
        email: user.email
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to resend verification email');
      logger.error('Resend verification email error:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const refreshUserData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const res = await axios.get(makeApiPath('/auth/user'));
      
      let userData: User;
      if (res.data && res.data._id) {
        userData = {
          id: String(res.data._id),
          username: res.data.username,
          email: res.data.email,
          trustedUser: res.data.trustedUser,
          isEmailVerified: res.data.isEmailVerified || false,
          cubePoints: res.data.cubePoints ?? 0,
          accountStatus: res.data.accountStatus
        };
        
        logger.info('AuthContext: refreshUserData mapped user', { id: userData.id });
      } else {
        userData = res.data as User;
      }
      
      setUser(userData);
      setToken(token);
      localStorage.setItem('cached_user', JSON.stringify(userData));
      localStorage.setItem('last_auth_check', Date.now().toString());
    } catch (err) {
      logger.error('Error refreshing user data:', err);
      throw err;
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    loading,
    error,
    login,
    register,
    logout,
    checkAuth,
    redirectAfterLogin,
    getRedirectPath,
    clearRedirectPath,
    clearError,
    resendVerificationEmail,
    refreshUserData,
    // Add the compatibility methods
    handleLogin,
    handleLogout
  };

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext; 